import { WidgetType } from '../../types';
import {
    HiveStackBaseUrls,
    HivestackAd,
    HivestackWidgetConfiguration,
    HivestackWidgetConfigurationDetails,
    HivestackWidgetTimelineSlot,
    ZodHivestackWidgetConfigurationDetails,
} from './types';
import AdQueue from '../AdQueue';
import { HivestackApiHelper } from './HivestackApiHelper';
import { HivestackVideoHandler } from '../../../MediaController/Video/Hivestack';
import { createHivestackVideoHandler } from '../../../MediaController';
import { BaseThirdPartyAdWidget } from '../BaseThirdPartyAdWidget';
import { ReachAPI } from '../../../ReachApi';
import { Log } from '../../../AuxillaryService/Log';
import { glom } from '../../../Signage/glom';
import { PlayerTokenError } from '../errors';
import { GetTokenAPISuccessResponse } from '../../../ReachApi/hivestackTypes';

export class HivestackWidget extends BaseThirdPartyAdWidget {
    type: WidgetType.HIVESTACK;
    declare config: HivestackWidgetConfigurationDetails;

    protected readonly adQueue: AdQueue<HivestackAd> = new AdQueue();

    /**
     * Boolean  determining whether widget user
     * is intending to fetch ads from a campaign registered with the Hivestack testing API (UAT)
     * or production ad serving api.
     */
    private isSandboxCampaign: boolean;

    private hivestackApiBaseUrl: HiveStackBaseUrls;

    /**
     * The uuid set in the Hivestack system to uniquely identify this
     * adunit.
     */
    private hivestackUuid: string | null;

    constructor(widgetConfiguration: HivestackWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);
        this.type = WidgetType.HIVESTACK;
        this.isSandboxCampaign = this.config.is_sandbox_campaign;
        this.hivestackApiBaseUrl = this.isSandboxCampaign
            ? HiveStackBaseUrls.TESTING
            : HiveStackBaseUrls.PRODUCTION;
        this.hivestackUuid = null;
    }

    protected validateConfigurationDetails(
        hivestackWidgetConfigurationDetails: HivestackWidgetConfigurationDetails
    ): HivestackWidgetConfigurationDetails {
        return ZodHivestackWidgetConfigurationDetails.parse(hivestackWidgetConfigurationDetails);
    }

    /**
     * send proof of play and remove played
     * ads from dom,  will also fetch ads if the current
     * slot it the last slot place exchange slot in the current loop
     */
    async afterFadeOut(currentSlot: HivestackWidgetTimelineSlot): Promise<void> {
        if (!currentSlot.params.ad.isFallback && currentSlot.params.ad.playcount >= 1) {
            await HivestackApiHelper.sendProofOfPlay(
                currentSlot.params.ad,
                this.hivestackUuid,
                this.id
            );
        }

        await super.afterFadeOut(currentSlot);
    }

    /**
     * Fetches Hivestack ads.
     */
    async getAds(): Promise<void> {
        await this.getHivestackCredentials();

        if (!this.hivestackUuid) {
            Log.Error(`${this.name}  uuid not set`, null, __fileline);
            return;
        }
        await HivestackApiHelper.fetchAds(
            this.hivestackUuid,
            this.numberOfAds,
            this.adQueue,
            this.hivestackApiBaseUrl
        );
    }

    /**
     * Get hivestack_uuid used for
     * api requests
     * @throws {@link PlayerTokenError} when token player token or UUID
     * isn't set
     * @throws {@link HivestackAPICredentialsError } when the reach API
     * fails to retrieve credentials stored for making Hivestack API requests.
     */

    private async getHivestackCredentials(): Promise<void> {
        if (!glom.playerUuid && !glom.securityToken) {
            throw new PlayerTokenError('Player uuid or security token must be set');
        }
        const security = new ReachAPI.Security(
            glom.playerUuid || undefined,
            glom.securityToken || undefined
        );
        try {
            const data: GetTokenAPISuccessResponse = await ReachAPI.hivestack.getCredentials(
                security,
                this.id
            );
            this.hivestackUuid = data.data.credentials.hivestack_uuid;
        } catch (error) {
            Log.Warning(`${this.name} : Failed to retrieve credentials`, null, __fileline);
        }
    }

    getThirdPartyAdVendorVideoHandler(assetUrl: string): HivestackVideoHandler {
        return createHivestackVideoHandler({
            videoUrl: assetUrl,
            muted: this.muteAds,
        });
    }
}
